import React, { useMemo, useEffect } from 'react';
import { FilterGrid, FilterField } from '@/components/ui';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import {
  ProjectWorksTaskFilterOptions,
  ProjectWorksTaskFilters
} from '../../../services/projectService';
import { formatStatusForDisplay } from '../../../utils/statusUtils';

interface ProjectWorksFiltersProps {
  filterOptions: ProjectWorksTaskFilterOptions;
  filters: ProjectWorksTaskFilters;
  onFilterChange: (name: string, value: string | string[] | boolean) => void;
  onClearFilters?: () => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  hasActiveFilters?: boolean;
}

const ProjectWorksFilters: React.FC<ProjectWorksFiltersProps> = ({
  filterOptions,
  filters,
  onFilterChange,
  onClearFilters,
  showApplyButton = false,
  onApply,
  applyDisabled = false,
  hasActiveFilters = false
}) => {
  const selectedForemanAreas = useMemo(() => {
    if (Array.isArray(filters.foremanArea)) {
      return filters.foremanArea.map((v) => v.toString());
    }
    if (filters.foremanArea) {
      return [filters.foremanArea.toString()];
    }
    return [];
  }, [filters.foremanArea]);

  const selectedStatuses = useMemo(() => {
    if (Array.isArray(filters.status)) {
      return filters.status;
    }
    if (filters.status) {
      return filters.status.split(',').filter(Boolean);
    }
    return [];
  }, [filters.status]);

  const filteredForemanAreas = useMemo(() => {
    if (!filters.serviceArea) {
      return filterOptions.foremanAreas;
    }
    return filterOptions.foremanAreas.filter((area) => {
      const serviceAreaName = area.serviceAreaName ?? '';
      const serviceAreaId = area.serviceAreaId?.toString() ?? '';
      return serviceAreaName === filters.serviceArea || serviceAreaId === filters.serviceArea;
    });
  }, [filterOptions.foremanAreas, filters.serviceArea]);

  const filteredProjects = useMemo(() => {
    if (!filters.serviceArea) {
      return filterOptions.projects;
    }
    return filterOptions.projects;
  }, [filterOptions.projects, filters.serviceArea]);

  useEffect(() => {
    if (selectedForemanAreas.length === 0) return;
    const valid = new Set(filteredForemanAreas.map((a) => a.name));
    const updated = selectedForemanAreas.filter((name) => valid.has(name));
    if (updated.length !== selectedForemanAreas.length) {
      onFilterChange('foremanArea', updated);
    }
  }, [filteredForemanAreas, onFilterChange, selectedForemanAreas]);

  return (
    <FiltersCard
      title="Project Task Filters"
      subtitle="Filter project tasks by service area, foreman area, contract year, activity, project, type, and status"
      collapsible={true}
      defaultCollapsed={false}
      showClearButton={true}
      onClear={onClearFilters}
      hasActiveFilters={hasActiveFilters}
      showApplyButton={showApplyButton}
      onApply={onApply}
      applyDisabled={applyDisabled}
    >
      <FilterGrid>
        <FilterField>
          <StandoutDropdown
            type="searchable"
            options={filterOptions.serviceAreas}
            value={filters.serviceArea || ''}
            onChange={(value) => onFilterChange('serviceArea', value)}
            getValue={(area) => area.id.toString()}
            formatOption={(area) => area.name}
            placeholder="All Service Areas"
            className="tw-w-full"
            label="Service Area"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={filteredForemanAreas}
            value={selectedForemanAreas}
            onChange={(value) => onFilterChange('foremanArea', value.map((v) => String(v)))}
            getValue={(area) => area.name}
            formatOption={(area) => area.name}
            formatDisplay={(area) => area.name}
            placeholder="All Foreman Areas"
            className="tw-w-full"
            label="Foreman Area"
          />
        </FilterField>
        <FilterField>
          <SearchableDropdown
            options={filterOptions.contractYears}
            value={filters.contractYear || ''}
            onChange={(value) => onFilterChange('contractYear', value)}
            getValue={(year) => year}
            formatOption={(year) => year}
            formatDisplay={(year) => year}
            placeholder="All Contract Years"
            className="tw-w-full"
            label="Contract Year"
            allowClear={true}
          />
        </FilterField>
        <FilterField>
          <SearchableDropdown
            options={filterOptions.activities}
            value={filters.activityId || ''}
            onChange={(value) => onFilterChange('activityId', value)}
            getValue={(a) => a.id}
            formatOption={(a) => `${a.activityNumber} - ${a.activityName}`}
            formatDisplay={(a) => `${a.activityNumber} - ${a.activityName}`}
            placeholder="All Activities"
            className="tw-w-full"
            label="Activity"
            allowClear={true}
          />
        </FilterField>
        <FilterField>
          <SearchableDropdown
            options={filteredProjects}
            value={filters.projectId || ''}
            onChange={(value) => onFilterChange('projectId', value)}
            getValue={(p) => p.id.toString()}
            formatOption={(p) => `${p.projectNumber} - ${p.name}`}
            formatDisplay={(p) => `${p.projectNumber} - ${p.name}`}
            placeholder="All Projects"
            className="tw-w-full"
            label="Project"
            allowClear={true}
          />
        </FilterField>
        <FilterField>
          <SearchableDropdown
            options={filterOptions.taskTypes}
            value={filters.taskType || ''}
            onChange={(value) => onFilterChange('taskType', value)}
            getValue={(t) => t}
            formatOption={(t) => t}
            formatDisplay={(t) => t}
            placeholder="All Types"
            className="tw-w-full"
            label="Task Type"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={filterOptions.statuses}
            value={selectedStatuses}
            onChange={(value) => onFilterChange('status', value.map((v) => String(v)))}
            getValue={(status) => status}
            formatOption={(status) => formatStatusForDisplay(status)}
            placeholder="All Statuses"
            className="tw-w-full"
            label="Status"
          />
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default ProjectWorksFilters;
